package dev.lingvo.shared.screenshots.frames

import dev.lingvo.shared.screenshots.doc.FrameKind
import dev.lingvo.shared.screenshots.geom.Rect
import dev.lingvo.shared.screenshots.render.ImageHandle
import dev.lingvo.shared.screenshots.render.Path
import dev.lingvo.shared.screenshots.render.PathOp
import dev.lingvo.shared.screenshots.render.Paths
import dev.lingvo.shared.screenshots.render.ResolvedPaint
import dev.lingvo.shared.screenshots.render.StrokeStyle
import dev.lingvo.shared.screenshots.render.Surface
import kotlin.math.max
import kotlin.math.roundToLong

/**
 * Paints hardware around a clipped COVER capture, isolating effects from the caller's canvas state.
 * Optional NONE hairlines use the caller's resolved [hairlineInk], since the shared options carry only background colour.
 */
fun paintFrame(
    surface: Surface,
    kind: FrameKind,
    layout: FrameLayout,
    options: FramePaintOptions,
    screen: ImageHandle?,
    hairline: Boolean = false,
    hairlineInk: Long = 0xFF000000,
) {
    val colours = frameColours(options.finish, options.paletteBg)
    val spec = FrameSpecs.of(kind)
    val s = layout.screen
    val rotated = s.w > s.h && kind !in listOf(FrameKind.LAPTOP, FrameKind.BROWSER, FrameKind.TV, FrameKind.NONE)
    val unit = if (rotated) s.h else s.w
    val b = spec.bezel
    val lid = if (rotated) Rect(s.x - b.top * unit, s.y - b.right * unit,
        s.w + (b.top + b.bottom) * unit, s.h + (b.left + b.right) * unit)
    else Rect(s.x - b.left * unit, s.y - b.top * unit,
        s.w + (b.left + b.right) * unit, s.h + (b.top + b.bottom) * unit)
    val rim = spec.rim * unit
    fun bodyPath(rect: Rect, radius: Double): Path = if (kind == FrameKind.WATCH_ROUND) Paths.ellipse(rect)
        else roundedRect(rect, max(0.0, radius), spec.smoothing)
    surface.save()
    try {
        surface.setShadow(null)
        layout.buttonPaths.forEach { surface.fill(it, ResolvedPaint.Solid(colours.body)) }
        surface.setShadow(options.shadow)
        surface.fill(layout.bodyPath, ResolvedPaint.Solid(colours.body))
        surface.setShadow(null)
        if (rim > 0) {
            surface.stroke(bodyPath(lid.inset(rim / 2), layout.outerRadius - rim / 2),
                ResolvedPaint.Linear(lid.x, lid.y, lid.right, lid.bottom,
                    listOf(0.0 to colours.rim, 1.0 to (colours.rim and 0xFFFFFF or 0x99000000))), StrokeStyle(rim))
        }
        if (kind != FrameKind.NONE) surface.fill(bodyPath(lid.inset(rim), layout.outerRadius - rim), ResolvedPaint.Solid(colours.bezel))
        surface.fill(layout.screenPath, ResolvedPaint.Solid(0xFF000000))
        if (screen != null) {
            require(screen.width > 0 && screen.height > 0) { "Capture dimensions must be positive" }
            surface.save()
            try {
                surface.clip(layout.screenPath)
                surface.drawImage(screen, coverSource(screen, s), s)
            } finally { surface.restore() }
        }
        layout.cutoutPath?.let { surface.fill(it, ResolvedPaint.Solid(0xFF000000)) }
        if (kind == FrameKind.FOLDABLE_OPEN) {
            layout.extras.firstOrNull()?.let { path ->
                val r = path.bounds()
                surface.fill(path, ResolvedPaint.Linear(r.x, r.y, if (rotated) r.x else r.right, if (rotated) r.bottom else r.y,
                    listOf(0.0 to 0x00000000, .5 to 0x14000000, 1.0 to 0x00000000)))
            }
        } else {
            layout.extras.forEach { surface.fill(it, ResolvedPaint.Solid(if (kind == FrameKind.BROWSER) colours.rim else 0xFF000000)) }
        }
        if (options.homeIndicator && kind in homeKinds) {
            val bar = if (rotated) Rect(s.right - .037 * unit, s.y + (s.h - .33 * unit) / 2, .012 * unit, .33 * unit)
                else Rect(s.x + (s.w - .33 * unit) / 2, s.bottom - .037 * unit, .33 * unit, .012 * unit)
            surface.fill(roundedRect(bar, .006 * unit, 0.0), ResolvedPaint.Solid(0x8CFFFFFF))
        }
        if (options.glare) surface.fill(layout.screenPath, ResolvedPaint.Linear(s.x, s.y, s.x + s.w / 3, s.y + s.h / 3,
            listOf(0.0 to 0x0FFFFFFF, 1.0 to 0x00FFFFFF)))
        if (kind == FrameKind.NONE && hairline) {
            val width = .004 * unit
            val alpha = (((hairlineInk shr 24) and 0xFF) * .1).roundToLong()
            val ink = (alpha shl 24) or (hairlineInk and 0xFFFFFF)
            surface.stroke(roundedRect(s.inset(width / 2), max(0.0, layout.screenRadius - width / 2), spec.smoothing),
                ResolvedPaint.Solid(ink), StrokeStyle(width))
        }
    } finally { surface.restore() }
}

private val homeKinds = setOf(FrameKind.PHONE_ISLAND, FrameKind.PHONE_NOTCH, FrameKind.PHONE_PUNCH_HOLE,
    FrameKind.PHONE_FLAT, FrameKind.TABLET_UNIFORM, FrameKind.TABLET_ANDROID, FrameKind.FOLDABLE_OPEN)

private fun coverSource(image: ImageHandle, dst: Rect): Rect {
    val factor = max(dst.w / image.width, dst.h / image.height)
    val w = dst.w / factor
    val h = dst.h / factor
    return Rect((image.width - w) / 2, (image.height - h) / 2, w, h)
}

private fun Rect.inset(d: Double) = Rect(x + d, y + d, max(0.0, w - 2 * d), max(0.0, h - 2 * d))

private fun Path.bounds(): Rect {
    val points = ops.mapNotNull { op -> when (op) {
        is PathOp.Move -> op.x to op.y
        is PathOp.Line -> op.x to op.y
        is PathOp.Cubic -> op.x to op.y
        else -> null
    } }
    val x = points.minOf { it.first }
    val y = points.minOf { it.second }
    return Rect(x, y, points.maxOf { it.first } - x, points.maxOf { it.second } - y)
}
